import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  History, RotateCcw, User, Clock, Plus, Pencil, Trash2, RefreshCw,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

const typeIcon = (t) => {
  switch (t) {
    case "create": return <Plus className="w-3 h-3 text-emerald-600" />;
    case "update": return <Pencil className="w-3 h-3 text-[#152D4B]" />;
    case "delete": return <Trash2 className="w-3 h-3 text-red-600" />;
    case "restore": return <RotateCcw className="w-3 h-3 text-amber-600" />;
    default: return <Clock className="w-3 h-3 text-zinc-400" />;
  }
};

const typeLabel = (t) => ({
  create: "Criada",
  update: "Editada",
  delete: "Excluída",
  restore: "Restaurada",
}[t] || t);

const fieldLabels = {
  title: "Título",
  type: "Tipo",
  responsible: "Responsável",
  presenter: "Apresentador",
  approved: "Aprovada",
  text: "Texto",
  tarja_chapeu: "Tarja (chapéu)",
  tarja_linha1: "Tarja (linha 1)",
  tarja_linha2: "Tarja (linha 2)",
  overlays_url: "Overlays URL",
  uppercase: "Caixa alta",
  vt_duration: "Duração VT",
};

export default function RetrancaHistoryDialog({
  episodeId, retrancaId, trigger, onRestored,
}) {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    if (!episodeId || !retrancaId) return;
    setLoading(true);
    try {
      const r = await api.get(`/episodes/${episodeId}/retrancas/${retrancaId}/history`);
      setEntries(r.data);
    } catch (e) {
      toast.error("Não consegui carregar o histórico.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, retrancaId, episodeId]);

  const restore = async (hid) => {
    if (!window.confirm("Restaurar o conteúdo desta versão? As alterações atuais serão sobrescritas.")) return;
    setRestoring(hid);
    try {
      const r = await api.post(`/episodes/${episodeId}/retrancas/${retrancaId}/restore/${hid}`);
      toast.success("Versão restaurada.");
      if (onRestored) onRestored(r.data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao restaurar.");
    } finally {
      setRestoring(null);
    }
  };

  return (
    <>
      <div onClick={() => setOpen(true)} className="inline-flex">
        {trigger}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-2xl" data-testid="retranca-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <History className="w-5 h-5 text-[#152D4B]" />
              Histórico de alterações
            </DialogTitle>
            <DialogDescription>
              Veja quem alterou esta retranca e restaure uma versão anterior se precisar.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">
              {entries.length} entrada{entries.length !== 1 ? "s" : ""}
            </div>
            <button
              onClick={load}
              className="text-xs text-zinc-500 hover:text-[#152D4B] flex items-center gap-1"
              data-testid="refresh-history-btn"
            >
              <RefreshCw className="w-3 h-3" /> Atualizar
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto border border-zinc-200 rounded-sm divide-y divide-zinc-100">
            {loading ? (
              <div className="p-8 text-center text-sm text-zinc-500">Carregando...</div>
            ) : entries.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-500">
                Nenhuma alteração registrada ainda.
              </div>
            ) : (
              entries.map((h, i) => (
                <div
                  key={h.id}
                  data-testid={`history-entry-${h.id}`}
                  className="p-3 flex items-start gap-3 hover:bg-zinc-50"
                >
                  <div className="w-6 h-6 flex items-center justify-center bg-zinc-100 rounded-sm flex-shrink-0">
                    {typeIcon(h.change_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-700 px-1.5 py-0.5 bg-zinc-100 rounded-sm">
                        {typeLabel(h.change_type)}
                      </span>
                      {i === 0 && (
                        <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">
                          atual
                        </span>
                      )}
                      <span className="text-xs text-zinc-600 flex items-center gap-1">
                        <User className="w-3 h-3" />
                        <span className="font-semibold">{h.user_name || h.user_email || "—"}</span>
                      </span>
                      <span className="text-[11px] text-zinc-400 flex items-center gap-1 ml-auto">
                        <Clock className="w-3 h-3" />
                        {h.changed_at ? format(parseISO(h.changed_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR }) : "—"}
                      </span>
                    </div>
                    {h.changed_fields && h.changed_fields.length > 0 && (
                      <div className="mt-1 text-[11px] text-zinc-500">
                        Campos: {h.changed_fields.map((f) => fieldLabels[f] || f).join(", ")}
                      </div>
                    )}
                    <div className="mt-1 text-xs text-zinc-700">
                      <span className="font-semibold">{h.snapshot?.title || "—"}</span>
                      {h.snapshot?.text && (
                        <span className="text-zinc-500 italic ml-2 line-clamp-1">
                          "{String(h.snapshot.text).slice(0, 120)}{String(h.snapshot.text).length > 120 ? "…" : ""}"
                        </span>
                      )}
                    </div>
                  </div>
                  {i !== 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`restore-history-${h.id}`}
                      onClick={() => restore(h.id)}
                      disabled={restoring === h.id}
                      className="rounded-sm h-7 text-xs border-[#152D4B] text-[#152D4B] hover:bg-[#152D4B] hover:text-white"
                    >
                      <RotateCcw className="w-3 h-3 mr-1" />
                      {restoring === h.id ? "..." : "Restaurar"}
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
